import {
  createFileRoute,
  Outlet,
  redirect,
  useNavigate,
  useRouterState,
} from '@tanstack/react-router';

import { sessionQueryOptions, useSession } from '@chariot/web-auth';
import { AppShell, Avatar, Button, Footer, Header, notify, type NavGroup } from '@chariot/web-ui';

/**
 * Every signed-in screen is a child of this layout. The guard runs before any
 * child loader: no session, no shell, straight to sign-in — with the path the
 * user wanted carried along so they land there afterwards.
 *
 * This is a usability gate, not the security one (architecture/04: permissions
 * gate rendering, not access). The server-side authorizer decides.
 */
export const Route = createFileRoute('/_app')({
  beforeLoad: async ({ context, location }) => {
    const session = await context.queryClient.query(sessionQueryOptions());
    if (session === null) {
      // TanStack Router's redirect is thrown by design; it is a Response, not an Error.
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw redirect({ to: '/login', search: { returnTo: location.href } });
    }
  },
  component: AppLayout,
});

function initials(email: string | undefined, name: string | undefined): string {
  const source = name ?? email ?? '';
  const parts = source.split(/[\s.@_-]+/).filter((part) => part.length > 0);
  return parts
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join('');
}

function AppLayout() {
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const { session, signOut } = useSession();

  const groups: NavGroup[] = [
    {
      id: 'primary',
      label: '',
      items: [
        { id: 'dashboard', label: 'Dashboard', icon: 'dashboard' },
        {
          id: 'matters',
          label: 'Matters',
          icon: 'matter',
          active: pathname === '/',
          onSelect: () => {
            void navigate({ to: '/' });
          },
        },
        { id: 'contacts', label: 'Contacts', icon: 'contact' },
        { id: 'documents', label: 'Documents', icon: 'document' },
      ],
    },
    {
      id: 'work',
      label: 'Work',
      items: [
        { id: 'vincent', label: 'Vincent', icon: 'book-open' },
        { id: 'editor', label: 'Editor', icon: 'note-pencil' },
        { id: 'library', label: 'Library', icon: 'library' },
        { id: 'docket', label: 'Docket', icon: 'docket' },
      ],
    },
    {
      id: 'manage',
      label: 'Manage',
      items: [
        { id: 'calendar', label: 'Calendar', icon: 'calendar' },
        { id: 'tasks', label: 'Tasks', icon: 'list-checks' },
        { id: 'activities', label: 'Activities', icon: 'clock' },
        { id: 'communications', label: 'Communications', icon: 'chat' },
        { id: 'app-integrations', label: 'App Integrations', icon: 'puzzle' },
        { id: 'settings', label: 'Settings', icon: 'settings' },
      ],
    },
  ];

  const toLogin = () => navigate({ to: '/login', search: { returnTo: undefined } });

  const onSignOut = () => {
    void signOut()
      .then(({ logoutUrl }) => {
        // Through Cognito's hosted-UI logout when the pool has one, so the identity
        // provider's session ends too; it lands back on /login. Otherwise straight there.
        if (logoutUrl !== null) {
          window.location.assign(logoutUrl);
          return;
        }
        return toLogin();
      })
      .catch(() => {
        // A sign-out that failed may or may not have revoked the session, so the
        // user is told what is actually known rather than shown a screen that
        // claims either. /login's guard re-reads the session from the server —
        // the cache is cleared by now — and returns the user to the app if it
        // turns out the session is still live.
        notify.error({
          title: 'Sign-out failed',
          description: 'Your session may still be open. Try again, or close the browser.',
        });
        return toLogin();
      });
  };

  return (
    <AppShell
      headerFixed
      footerFixed
      footerSize="sm"
      header={
        <Header
          logo={<h1 className="text-card-title font-semibold">Chariot</h1>}
          actions={
            <>
              <Button variant="ghost" size="icon" icon="notification" aria-label="Notifications" />
              <Button variant="ghost" size="icon" icon="theme" aria-label="Toggle theme" />
              <Avatar
                size="sm"
                fallback={initials(session?.user.email, session?.user.name) || 'CR'}
              />
              <Button variant="ghost" size="sm" onClick={onSignOut}>
                Sign out
              </Button>
            </>
          }
        />
      }
      footer={<Footer copyright={<span>© {new Date().getFullYear()} Chariot</span>} size="sm" />}
      sidebar={{ groups }}
    >
      <div className="p-page">
        <Outlet />
      </div>
    </AppShell>
  );
}
